// Manifest fields keep the snake_case names written by the Python converter.
/* eslint-disable @typescript-eslint/naming-convention */

// Client-side traversal of an hlod-tileset photogrammetry model. Every frame the tree is walked
// from the root: a node is drawn when its projected error is small enough or its children are not
// loaded yet, otherwise its children replace it. Only the nodes the current view needs are
// requested, highest screen error first, so the first image appears after the root and the
// network and GPU work stay bounded by the screen size rather than by the model size.
//
// Two refinements on top of that:
// - Memory budget. Nodes that stop being drawn stay loaded ("hidden", not "unloaded") until GPU and
//   CPU bytes exceed the budget; then nodes the camera would not draw from here go first, then the
//   least recently used ones (src/shared/streaming/tile-cache-eviction.function.ts). The coarsest
//   levels are never released.
//   When the nodes the view draws do not fit either, the error threshold grows until they do, and
//   shrinks back slowly once memory frees up, so the budget holds on phones in dense views.
// - Occlusion. The caller reports which drawn nodes had visible pixels (core HsTileVisibilityPass).
//   A node found hidden by several consecutive checks keeps its current level: its children are
//   not requested, but nothing already loaded is dropped, so turning around never shows holes.

import { hsChooseTileEvictions, type HsTileCacheEntry } from '../../../shared/streaming/tile-cache-eviction.function';
import type { HsBoundingBoxView } from '@sibe/hyperscene';
import { HsOcclusionHysteresis } from '../../../shared/culling/occlusion-hysteresis.class';

export const HS_HLOD_REGION_ID: string = 'hlod';

/** Link of a photogrammetry root mesh to its tileset manifest, as returned by the API. */
export const HS_HLOD_MANIFEST_FORMAT: string = 'hyperscene-hlod-tileset';

export interface HsHlodNodeRecord {
  id: string;
  /** Payload-frame bounds: min x, y, z, max x, y, z. */
  bounds: [number, number, number, number, number, number];
  /** Geometric error in payload units. */
  error: number;
  faces: number;
  texture: [number, number];
  /** Document id of the node payload. */
  content: string;
  bytes: number;
  children?: string[];
  /** Document id of the manifest that lists this node's children. */
  subtree?: string;
}

export interface HsHlodTilesetManifest {
  format: string;
  version: number;
  root: string;
  nodes: HsHlodNodeRecord[];
}

export interface HsHlodPlane {
  normal: { x: number; y: number; z: number };
  offset: number;
}

export interface HsHlodView {
  cameraPosition: readonly [number, number, number];
  /** World-space frustum planes; a point is inside when normal · p + offset >= 0. */
  planes: readonly HsHlodPlane[];
  /** Viewport height / (2 tan(fov / 2)): screen pixels per world unit at distance 1. */
  focalPixels: number;
  near: number;
}

export interface HsHlodTilesetIo<H> {
  /** Downloads up to 100 documents. */
  download(documentIds: string[]): Promise<Map<string, ArrayBuffer>>;
  /** Decodes a payload off the critical path (inflate, image decode). */
  decode(record: HsHlodNodeRecord, payload: ArrayBuffer): Promise<unknown>;
  /** Uploads a decoded node to the GPU; runs inside update() under a per-frame budget. */
  upload(record: HsHlodNodeRecord, decoded: unknown): { handle: H; bytes: number } | null;
  /** Releases a decoded node that was never uploaded, for example an ImageBitmap. */
  discard?(decoded: unknown): void;
  /** CPU bytes a decoded node holds until it is uploaded; counted towards the budget. */
  decodedBytes?(decoded: unknown): number;
  dispose(handle: H): void;
}

export interface HsHlodTilesetOptions {
  maxScreenErrorPixels: number;
  maxConcurrentRequests: number;
  maxNodesPerRequest: number;
  maxBytesPerRequest: number;
  /** Budget for loaded nodes (GPU) plus decoded nodes waiting for upload (CPU). */
  gpuBudgetBytes: number;
  uploadBudgetMs: number;
  retryDelayMs: number;
  /** Nodes shallower than this depth are never evicted; 1 pins the root. */
  pinnedDepth: number;
  /** Whether nodes reported hidden keep their level instead of requesting children. */
  occlusionRefinement: boolean;
  /** Consecutive hidden checks before a node counts as occluded. */
  occlusionChecks: number;
}

export const HS_DEFAULT_HLOD_TILESET_OPTIONS: HsHlodTilesetOptions = {
  maxScreenErrorPixels: 3,
  maxConcurrentRequests: 6,
  maxNodesPerRequest: 4,
  maxBytesPerRequest: 4 * 1024 * 1024,
  gpuBudgetBytes: 1024 * 1024 * 1024,
  uploadBudgetMs: 6,
  retryDelayMs: 5000,
  pinnedDepth: 1,
  occlusionRefinement: true,
  occlusionChecks: 3,
};

export interface HsHlodTilesetStats {
  nodes: number;
  rendered: number;
  renderedFaces: number;
  resident: number;
  /** Loaded nodes that are not drawn this frame; they are kept until the budget needs the bytes. */
  hidden: number;
  residentBytes: number;
  /** Decoded nodes waiting for upload. */
  decodedBytes: number;
  /** Drawn nodes the last visibility checks found hidden; they are not refined. */
  occluded: number;
  /** Multiplier on the error threshold while the drawn nodes alone exceed the budget. */
  errorScale: number;
  loading: number;
  queued: number;
  downloadedBytes: number;
  failed: number;
  /** Node payloads requested since the tileset was created. */
  requests: number;
  /** Requests for nodes that had been loaded before and were evicted since. */
  refetches: number;
  evictions: number;
  peakResidentBytes: number;
}

/** What a tileset still has to do before the current view is drawn at its target detail. */
export interface HsHlodTilesetLoadStatus {
  /** The root node is drawn, so the tileset replaces its root mesh. */
  rootReady: boolean;
  /** The last update's view met the root bounds, so the root is wanted; true before the first update. */
  rootInView: boolean;
  /** Subtree manifests downloading. */
  subtreesLoading: number;
  /** Nodes requested and not decoded yet. */
  nodesLoading: number;
  /** Nodes the last update wanted but has not requested yet. */
  nodesQueued: number;
  /** Nodes decoded and waiting for their GPU upload. */
  nodesDecoded: number;
  /** Nodes whose download or upload failed, including those waiting for a retry. */
  nodesFailed: number;
  /** Nodes or subtrees the last update wanted that wait for their retry time. */
  nodesRetrying: number;
  /** Node batches and subtree manifests in flight. */
  requestsInFlight: number;
  downloadedBytes: number;
}

export interface HsHlodUpdateResult<H> {
  /** Handles to draw this frame; the caller hides every other handle. */
  render: H[];
  /** Whether the drawn set differs from the previous update. */
  changed: boolean;
  /** Work is still in flight or waiting, so a later update will change the result. */
  pending: boolean;
  /** Decoded nodes wait for upload, which only the next update does. */
  needsFrame: boolean;
}

type NodeStatus = 'idle' | 'loading' | 'decoded' | 'ready' | 'failed';

interface NodeState<H> {
  record: HsHlodNodeRecord;
  parent: NodeState<H> | null;
  children: NodeState<H>[] | null;
  subtreeLoading: boolean;
  status: NodeStatus;
  decoded: unknown;
  handle: H | null;
  bytes: number;
  decodedBytes: number;
  lastUsed: number;
  /** Earliest time the payload is requested again after a download or upload failure. */
  retryAt: number;
  /** Earliest time the subtree manifest is requested again after a failure; kept apart from the payload's. */
  subtreeRetryAt: number;
  everLoaded: boolean;
  // World-space bounding sphere, cached for the transform it was computed with.
  sphereVersion: number;
  center: [number, number, number];
  radius: number;
}

const MAX_ERROR_SCALE: number = 64;

interface ResidentNode<H> extends HsTileCacheEntry {
  node: NodeState<H>;
}

interface Want<H> {
  node: NodeState<H>;
  priority: number;
}

export class HsHlodTileset<H> {
  private readonly nodes: Map<string, NodeState<H>> = new Map<string, NodeState<H>>();
  private readonly root: NodeState<H>;
  private readonly matrix: number[] = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  private matrixVersion: number = 0;
  private scale: number = 1;
  private frame: number = 0;
  private inFlight: number = 0;
  private wanted: Want<H>[] = [];
  /** Failed nodes and subtrees the last update wanted but had to wait for; see `loadStatus`. */
  private retrying: number = 0;
  /** Earliest retry time among them; a frame is scheduled for it, since a still camera draws none by itself. */
  private nextRetryAt: number = Number.POSITIVE_INFINITY;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimerAt: number = Number.POSITIVE_INFINITY;
  private rootInView: boolean = true;
  private rendered: Set<NodeState<H>> = new Set<NodeState<H>>();
  private downloadedBytes: number = 0;
  private requests: number = 0;
  private refetches: number = 0;
  private evictions: number = 0;
  private residentBytes: number = 0;
  private peakResidentBytes: number = 0;
  private lastView: HsHlodView | null = null;
  private errorScale: number = 1;
  private readonly occlusion: HsOcclusionHysteresis<NodeState<H>> = new HsOcclusionHysteresis<NodeState<H>>();
  private readonly nodesByHandle: Map<H, NodeState<H>> = new Map<H, NodeState<H>>();
  private disposed: boolean = false;

  constructor(
    manifest: HsHlodTilesetManifest,
    private readonly io: HsHlodTilesetIo<H>,
    private readonly onProgress: () => void,
    public options: HsHlodTilesetOptions = HS_DEFAULT_HLOD_TILESET_OPTIONS
  ) {
    assertManifest(manifest);
    this.addNodes(manifest, null);
    const root: NodeState<H> | undefined = this.nodes.get(manifest.root);
    if (root === undefined) {
      throw new Error(`HLOD manifest does not list its root ${manifest.root}.`);
    }
    this.root = root;
  }

  public get rootRecord(): HsHlodNodeRecord {
    return this.root.record;
  }

  public get rootReady(): boolean {
    return this.root.status === 'ready';
  }

  /** World-space axis-aligned bounds of the whole tileset under the current transform. */
  public worldBounds(): HsBoundingBoxView {
    return this.worldBox(this.root.record.bounds);
  }

  /**
   * Lowest world Y of the deepest node whose world bounds cover (x, z) in the XZ plane, with that node's
   * depth; null when no node covers the point. Walk-through cameras use it to stay above the terrain.
   */
  public groundHeightAt(x: number, z: number): { depth: number; y: number } | null {
    let best: { depth: number; y: number } | null = null;
    for (const node of this.nodes.values()) {
      const box: HsBoundingBoxView = this.worldBox(node.record.bounds);
      if (x < box.min[0] || x > box.max[0] || z < box.min[2] || z > box.max[2]) {
        continue;
      }
      let depth: number = 0;
      for (let parent: NodeState<H> | null = node.parent; parent !== null; parent = parent.parent) {
        depth++;
      }
      if (best === null || depth > best.depth) {
        best = { depth, y: box.min[1] };
      }
    }
    return best;
  }

  /** Column-major payload-to-world transform. */
  public setTransform(matrix: readonly number[]): void {
    if (matrix.length !== 16 || matrix.every((value: number, index: number) => value === this.matrix[index])) {
      return;
    }
    matrix.forEach((value: number, index: number) => {
      this.matrix[index] = value;
    });
    this.scale = Math.max(
      Math.hypot(matrix[0], matrix[1], matrix[2]),
      Math.hypot(matrix[4], matrix[5], matrix[6]),
      Math.hypot(matrix[8], matrix[9], matrix[10])
    );
    this.matrixVersion++;
  }

  public update(view: HsHlodView): HsHlodUpdateResult<H> {
    this.frame++;
    this.lastView = view;
    this.occlusion.occludedChecks = this.options.occlusionChecks;
    this.uploadDecoded();
    this.retrying = 0;
    this.nextRetryAt = Number.POSITIVE_INFINITY;
    const render: NodeState<H>[] = [];
    const wanted: Want<H>[] = [];
    this.select(this.root, view, render, wanted);
    this.rootInView = this.intersects(this.root, view);
    const next: Set<NodeState<H>> = new Set<NodeState<H>>(render);
    const changed: boolean =
      next.size !== this.rendered.size || render.some((node: NodeState<H>) => !this.rendered.has(node));
    this.rendered = next;
    this.protect(render);
    this.wanted = wanted.sort((a: Want<H>, b: Want<H>) => b.priority - a.priority);
    this.pump();
    this.evict();
    this.adaptErrorScale();
    this.scheduleRetryFrame();
    return {
      render: render.map((node: NodeState<H>) => node.handle as H),
      changed,
      pending: this.inFlight > 0 || this.wanted.length > 0 || this.hasDecoded(),
      needsFrame: this.hasDecoded(),
    };
  }

  /**
   * Records a visibility check of drawn nodes: `checked` were drawn in the check, `visible` had at
   * least one pixel. Returns whether a node changed between occluded and visible, so the caller
   * knows another selection is due.
   */
  public applyVisibility(checked: Iterable<H>, visible: ReadonlySet<H>): boolean {
    const nodes: NodeState<H>[] = [];
    const visibleNodes: Set<NodeState<H>> = new Set<NodeState<H>>();
    for (const handle of checked) {
      const node: NodeState<H> | undefined = this.nodesByHandle.get(handle);
      if (node !== undefined) {
        nodes.push(node);
        if (visible.has(handle)) {
          visibleNodes.add(node);
        }
      }
    }
    const before: boolean[] = nodes.map((node: NodeState<H>) => this.occlusion.isOccluded(node));
    this.occlusion.observe(nodes, visibleNodes);
    return nodes.some((node: NodeState<H>, index: number) => this.occlusion.isOccluded(node) !== before[index]);
  }

  /** Whether some drawn node is between its first hidden check and being counted as occluded. */
  public get occlusionUnsettled(): boolean {
    for (const node of this.rendered) {
      if (this.occlusion.isPending(node)) {
        return true;
      }
    }
    return false;
  }

  /** The work left for the last update's view; `nodesRetrying` and `nodesQueued` are as of that update. */
  public loadStatus(): HsHlodTilesetLoadStatus {
    let subtreesLoading: number = 0;
    let nodesLoading: number = 0;
    let nodesDecoded: number = 0;
    let nodesFailed: number = 0;
    this.nodes.forEach((node: NodeState<H>) => {
      if (node.subtreeLoading) {
        subtreesLoading++;
      }
      if (node.status === 'loading') {
        nodesLoading++;
      } else if (node.status === 'decoded') {
        nodesDecoded++;
      } else if (node.status === 'failed') {
        nodesFailed++;
      }
    });
    return {
      rootReady: this.rootReady,
      rootInView: this.rootInView,
      subtreesLoading,
      nodesLoading,
      nodesQueued: this.wanted.length,
      nodesDecoded,
      nodesFailed,
      nodesRetrying: this.retrying,
      requestsInFlight: this.inFlight,
      downloadedBytes: this.downloadedBytes,
    };
  }

  public stats(): HsHlodTilesetStats {
    let resident: number = 0;
    let residentBytes: number = 0;
    let decodedBytes: number = 0;
    let loading: number = 0;
    let failed: number = 0;
    this.nodes.forEach((node: NodeState<H>) => {
      if (node.status === 'ready') {
        resident++;
        residentBytes += node.bytes;
      } else if (node.status === 'loading' || node.status === 'decoded') {
        loading++;
        decodedBytes += node.decodedBytes;
      } else if (node.status === 'failed') {
        failed++;
      }
    });
    let renderedFaces: number = 0;
    let occluded: number = 0;
    this.rendered.forEach((node: NodeState<H>) => {
      renderedFaces += node.record.faces;
      occluded += this.occlusion.isOccluded(node) ? 1 : 0;
    });
    return {
      nodes: this.nodes.size,
      rendered: this.rendered.size,
      renderedFaces,
      resident,
      hidden: resident - this.rendered.size,
      residentBytes,
      decodedBytes,
      occluded,
      errorScale: this.errorScale,
      loading,
      queued: this.wanted.length,
      downloadedBytes: this.downloadedBytes,
      failed,
      requests: this.requests,
      refetches: this.refetches,
      evictions: this.evictions,
      peakResidentBytes: this.peakResidentBytes,
    };
  }

  /**
   * Drops the nodes decoded but not uploaded yet, for a tileset whose root mesh left the scene: without updates
   * nothing would upload or release them. They are requested again when a view wants them.
   */
  public discardDecoded(): void {
    this.nodes.forEach((node: NodeState<H>) => {
      if (node.status === 'decoded') {
        this.release(node);
      }
    });
  }

  public dispose(): void {
    this.disposed = true;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.nodes.forEach((node: NodeState<H>) => this.release(node));
    this.rendered.clear();
    this.occlusion.clear();
    this.wanted = [];
  }

  private select(node: NodeState<H>, view: HsHlodView, render: NodeState<H>[], wanted: Want<H>[]): void {
    this.updateSphere(node);
    if (!this.intersects(node, view)) {
      return;
    }
    node.lastUsed = this.frame;
    const screenError: number = this.screenError(node, view);
    const refine: boolean =
      screenError > this.errorThreshold &&
      (node.children !== null ? node.children.length > 0 : node.record.subtree !== undefined);
    if (!refine) {
      this.use(node, render, wanted);
      return;
    }
    // A drawn node that stays hidden behind other surfaces keeps its level; whatever is already
    // loaded below it is still used, so this only saves downloads, never removes detail.
    const occluded: boolean = this.options.occlusionRefinement && this.occlusion.isOccluded(node);
    if (node.children === null) {
      if (!occluded) {
        this.loadSubtree(node);
      }
      this.use(node, render, wanted);
      return;
    }
    // Children replace the parent only once every visible child can be drawn, so the surface
    // never has holes and never mixes overlapping levels.
    const visible: NodeState<H>[] = node.children.filter((child: NodeState<H>) => {
      this.updateSphere(child);
      return this.intersects(child, view);
    });
    const missing: NodeState<H>[] = visible.filter((child: NodeState<H>) => child.status !== 'ready');
    if (missing.length === 0) {
      visible.forEach((child: NodeState<H>) => this.select(child, view, render, wanted));
      return;
    }
    // Loaded siblings count as used too: evicting them while the rest load would never let the
    // parent be replaced.
    visible.forEach((child: NodeState<H>) => {
      child.lastUsed = this.frame;
    });
    if (!occluded) {
      // A node already found hidden once waits behind every visible request, so its children are
      // rarely downloaded before the remaining checks confirm the occlusion.
      const priority: number =
        this.options.occlusionRefinement && this.occlusion.isPending(node) ? screenError * 1e-6 : screenError;
      missing.forEach((child: NodeState<H>) => this.want(child, priority, wanted));
    }
    this.use(node, render, wanted);
  }

  private use(node: NodeState<H>, render: NodeState<H>[], wanted: Want<H>[]): void {
    if (node.status === 'ready') {
      render.push(node);
      return;
    }
    // Nothing drawable here yet: keep whatever was drawn below this node so zooming out after an
    // eviction does not blank the area; such requests outrank refinements.
    this.want(node, Number.MAX_SAFE_INTEGER - this.depth(node), wanted);
    this.rendered.forEach((drawn: NodeState<H>) => {
      if (drawn.status === 'ready' && this.isAncestor(node, drawn)) {
        drawn.lastUsed = this.frame;
        render.push(drawn);
      }
    });
  }

  private want(node: NodeState<H>, priority: number, wanted: Want<H>[]): void {
    if (node.status === 'idle' || (node.status === 'failed' && performance.now() >= node.retryAt)) {
      wanted.push({ node, priority });
    } else if (node.status === 'failed') {
      this.waitForRetry(node.retryAt);
    }
  }

  private waitForRetry(at: number): void {
    this.retrying++;
    this.nextRetryAt = Math.min(this.nextRetryAt, at);
  }

  /**
   * Asks for a frame when the earliest retry the last update waited for comes due. The viewer only updates the
   * tileset while it draws, so a failed node in front of a still camera would otherwise wait for the next
   * interaction.
   */
  private scheduleRetryFrame(): void {
    const at: number = this.nextRetryAt;
    if (this.retryTimer !== null) {
      if (at === this.retryTimerAt) {
        return;
      }
      // The earliest retry moved (requested, or a later one is next): re-arm or cancel the timer.
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (at === Number.POSITIVE_INFINITY) {
      return;
    }
    this.retryTimerAt = at;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.retryTimerAt = Number.POSITIVE_INFINITY;
      if (!this.disposed) {
        this.onProgress();
      }
    }, Math.max(0, at - performance.now()));
  }

  private screenError(node: NodeState<H>, view: HsHlodView): number {
    const distance: number = Math.max(
      Math.hypot(
        node.center[0] - view.cameraPosition[0],
        node.center[1] - view.cameraPosition[1],
        node.center[2] - view.cameraPosition[2]
      ) - node.radius,
      view.near
    );
    return (node.record.error * this.scale * view.focalPixels) / distance;
  }

  private intersects(node: NodeState<H>, view: HsHlodView): boolean {
    return view.planes.every(
      (plane: HsHlodPlane) =>
        plane.normal.x * node.center[0] +
          plane.normal.y * node.center[1] +
          plane.normal.z * node.center[2] +
          plane.offset >=
        -node.radius
    );
  }

  private updateSphere(node: NodeState<H>): void {
    if (node.sphereVersion === this.matrixVersion) {
      return;
    }
    const b: HsHlodNodeRecord['bounds'] = node.record.bounds;
    const x: number = (b[0] + b[3]) / 2;
    const y: number = (b[1] + b[4]) / 2;
    const z: number = (b[2] + b[5]) / 2;
    const m: number[] = this.matrix;
    node.center = [
      m[0] * x + m[4] * y + m[8] * z + m[12],
      m[1] * x + m[5] * y + m[9] * z + m[13],
      m[2] * x + m[6] * y + m[10] * z + m[14],
    ];
    node.radius = (Math.hypot(b[3] - b[0], b[4] - b[1], b[5] - b[2]) / 2) * this.scale;
    node.sphereVersion = this.matrixVersion;
  }

  private pump(): void {
    const queue: Want<H>[] = this.wanted.filter((want: Want<H>) => want.node.status !== 'loading');
    while (this.inFlight < this.options.maxConcurrentRequests && queue.length > 0) {
      const batch: NodeState<H>[] = [];
      let bytes: number = 0;
      while (
        queue.length > 0 &&
        batch.length < this.options.maxNodesPerRequest &&
        (batch.length === 0 || bytes + queue[0].node.record.bytes <= this.options.maxBytesPerRequest)
      ) {
        const node: NodeState<H> = (queue.shift() as Want<H>).node;
        if (node.status === 'idle' || node.status === 'failed') {
          node.status = 'loading';
          this.requests++;
          if (node.everLoaded) {
            this.refetches++;
          }
          bytes += node.record.bytes;
          batch.push(node);
        }
      }
      if (batch.length > 0) {
        void this.load(batch);
      }
    }
    this.wanted = queue;
  }

  private async load(batch: NodeState<H>[]): Promise<void> {
    this.inFlight++;
    try {
      const documents: Map<string, ArrayBuffer> = await this.io.download(
        batch.map((node: NodeState<H>) => node.record.content)
      );
      await Promise.all(
        batch.map(async (node: NodeState<H>) => {
          const payload: ArrayBuffer | undefined = documents.get(node.record.content);
          if (payload === undefined) {
            throw new Error(`HLOD node ${node.record.id} was not returned.`);
          }
          this.downloadedBytes += payload.byteLength;
          const decoded: unknown = await this.io.decode(node.record, payload);
          if (this.disposed) {
            this.io.discard?.(decoded);
            return;
          }
          node.decoded = decoded;
          node.decodedBytes = this.io.decodedBytes?.(decoded) ?? 0;
          node.status = 'decoded';
        })
      );
    } catch (error: unknown) {
      console.warn('HLOD node download failed.', error);
      batch.forEach((node: NodeState<H>) => {
        if (node.status === 'loading') {
          node.status = 'failed';
          node.retryAt = performance.now() + this.options.retryDelayMs;
        }
      });
    } finally {
      this.inFlight--;
      if (!this.disposed) {
        this.onProgress();
      }
    }
  }

  private uploadDecoded(): void {
    const started: number = performance.now();
    // Uploading is the main-thread cost; coarse nodes first so the view fills in quickly.
    const decoded: NodeState<H>[] = Array.from(this.nodes.values())
      .filter((node: NodeState<H>) => node.status === 'decoded')
      .sort((a: NodeState<H>, b: NodeState<H>) => this.depth(a) - this.depth(b));
    for (const node of decoded) {
      if (performance.now() - started > this.options.uploadBudgetMs && node !== decoded[0]) {
        break;
      }
      const uploaded: { handle: H; bytes: number } | null = this.io.upload(node.record, node.decoded);
      node.decoded = null;
      node.decodedBytes = 0;
      if (uploaded === null) {
        node.status = 'failed';
        node.retryAt = performance.now() + this.options.retryDelayMs;
        continue;
      }
      node.handle = uploaded.handle;
      node.bytes = uploaded.bytes;
      node.status = 'ready';
      node.everLoaded = true;
      this.nodesByHandle.set(uploaded.handle, node);
      this.residentBytes += uploaded.bytes;
      this.peakResidentBytes = Math.max(this.peakResidentBytes, this.residentBytes);
      node.lastUsed = this.frame;
    }
  }

  private hasDecoded(): boolean {
    for (const node of this.nodes.values()) {
      if (node.status === 'decoded') {
        return true;
      }
    }
    return false;
  }

  // Ancestors of drawn nodes stay resident: they are small and make zooming out instant.
  private protect(render: NodeState<H>[]): void {
    render.forEach((node: NodeState<H>) => {
      for (let current: NodeState<H> | null = node; current !== null; current = current.parent) {
        current.lastUsed = this.frame;
      }
    });
  }

  private get errorThreshold(): number {
    return this.options.maxScreenErrorPixels * this.errorScale;
  }

  /**
   * Coarsens the selection while what this frame needs (drawn nodes, the ancestors and siblings
   * kept for them, decoded nodes) exceeds the budget; relaxes once there is room. Hidden nodes do
   * not count: the cache keeps them only while nothing else needs the memory.
   */
  private adaptErrorScale(): void {
    let needed: number = 0;
    this.nodes.forEach((node: NodeState<H>) => {
      needed += node.decodedBytes + (node.status === 'ready' && node.lastUsed === this.frame ? node.bytes : 0);
    });
    const budget: number = this.options.gpuBudgetBytes;
    if (needed > budget) {
      this.errorScale = Math.min(this.errorScale * 1.05, MAX_ERROR_SCALE);
    } else if (needed < budget * 0.75) {
      this.errorScale = Math.max(1, this.errorScale / 1.02);
    }
  }

  private evict(): void {
    let total: number = 0;
    this.nodes.forEach((node: NodeState<H>) => {
      total += node.decodedBytes + (node.status === 'ready' ? node.bytes : 0);
    });
    if (total <= this.options.gpuBudgetBytes) {
      return;
    }
    const resident: ResidentNode<H>[] = [];
    this.nodes.forEach((node: NodeState<H>) => {
      if (node.status === 'ready') {
        resident.push({
          node,
          bytes: node.bytes,
          lastUsed: node.lastUsed,
          need: this.need(node),
          pinned: this.depth(node) < this.options.pinnedDepth,
        });
      }
    });
    hsChooseTileEvictions(resident, total, this.options.gpuBudgetBytes, this.frame).forEach(
      (entry: ResidentNode<H>) => {
        this.evictions++;
        this.release(entry.node);
      }
    );
  }

  /** The parent's screen error over the threshold, from the current camera position in any direction. */
  private need(node: NodeState<H>): number {
    if (node.parent === null || this.lastView === null) {
      return Number.POSITIVE_INFINITY;
    }
    this.updateSphere(node.parent);
    return this.screenError(node.parent, this.lastView) / this.errorThreshold;
  }

  private release(node: NodeState<H>): void {
    if (node.handle !== null) {
      this.io.dispose(node.handle);
      this.nodesByHandle.delete(node.handle);
      this.residentBytes -= node.bytes;
    }
    this.occlusion.forget(node);
    if (node.decoded !== null) {
      this.io.discard?.(node.decoded);
    }
    node.handle = null;
    node.decoded = null;
    node.bytes = 0;
    node.decodedBytes = 0;
    if (node.status === 'ready' || node.status === 'decoded') {
      node.status = 'idle';
    }
  }

  private loadSubtree(node: NodeState<H>): void {
    const subtree: string | undefined = node.record.subtree;
    if (subtree === undefined || node.subtreeLoading) {
      return;
    }
    if (performance.now() < node.subtreeRetryAt) {
      this.waitForRetry(node.subtreeRetryAt);
      return;
    }
    node.subtreeLoading = true;
    this.inFlight++;
    void this.io
      .download([subtree])
      .then((documents: Map<string, ArrayBuffer>) => {
        const data: ArrayBuffer | undefined = documents.get(subtree);
        if (data === undefined) {
          throw new Error(`HLOD subtree ${subtree} was not returned.`);
        }
        this.downloadedBytes += data.byteLength;
        const manifest: HsHlodTilesetManifest = JSON.parse(new TextDecoder().decode(data)) as HsHlodTilesetManifest;
        assertManifest(manifest);
        if (manifest.root !== node.record.id) {
          throw new Error(`HLOD subtree ${subtree} starts at ${manifest.root}, not ${node.record.id}.`);
        }
        this.addNodes(manifest, node);
      })
      .catch((error: unknown) => {
        console.warn('HLOD subtree download failed.', error);
        node.subtreeRetryAt = performance.now() + this.options.retryDelayMs;
      })
      .finally(() => {
        node.subtreeLoading = false;
        this.inFlight--;
        if (!this.disposed) {
          this.onProgress();
        }
      });
  }

  /** Adds a manifest's nodes; a subtree's first node is `attachTo`, which gains its children. */
  private addNodes(manifest: HsHlodTilesetManifest, attachTo: NodeState<H> | null): void {
    const records: Map<string, HsHlodNodeRecord> = new Map<string, HsHlodNodeRecord>(
      manifest.nodes.map((record: HsHlodNodeRecord) => [record.id, record])
    );
    const states: Map<string, NodeState<H>> = new Map<string, NodeState<H>>();
    records.forEach((record: HsHlodNodeRecord, id: string) => {
      const existing: NodeState<H> | undefined = id === attachTo?.record.id ? attachTo : this.nodes.get(id);
      states.set(id, existing ?? this.createState(record));
    });
    records.forEach((record: HsHlodNodeRecord, id: string) => {
      const state: NodeState<H> = states.get(id) as NodeState<H>;
      if (record.children !== undefined) {
        state.children = record.children.map((childId: string) => {
          const child: NodeState<H> | undefined = states.get(childId);
          if (child === undefined) {
            throw new Error(`HLOD manifest lists child ${childId} of ${id} without its record.`);
          }
          child.parent = state;
          return child;
        });
      } else if (record.subtree === undefined && state.children === null) {
        state.children = [];
      }
      this.nodes.set(id, state);
    });
  }

  private createState(record: HsHlodNodeRecord): NodeState<H> {
    return {
      record,
      parent: null,
      children: null,
      subtreeLoading: false,
      status: 'idle',
      decoded: null,
      handle: null,
      bytes: 0,
      decodedBytes: 0,
      lastUsed: 0,
      retryAt: 0,
      subtreeRetryAt: 0,
      everLoaded: false,
      sphereVersion: -1,
      center: [0, 0, 0],
      radius: 0,
    };
  }

  private depth(node: NodeState<H>): number {
    let depth: number = 0;
    for (let current: NodeState<H> | null = node.parent; current !== null; current = current.parent) {
      depth++;
    }
    return depth;
  }

  private isAncestor(ancestor: NodeState<H>, node: NodeState<H>): boolean {
    for (let current: NodeState<H> | null = node.parent; current !== null; current = current.parent) {
      if (current === ancestor) {
        return true;
      }
    }
    return false;
  }

  private worldBox(bounds: HsHlodNodeRecord['bounds']): HsBoundingBoxView {
    const min: number[] = [Infinity, Infinity, Infinity];
    const max: number[] = [-Infinity, -Infinity, -Infinity];
    const m: number[] = this.matrix;
    for (const x of [bounds[0], bounds[3]]) {
      for (const y of [bounds[1], bounds[4]]) {
        for (const z of [bounds[2], bounds[5]]) {
          for (let axis: number = 0; axis < 3; axis++) {
            const value: number = m[axis] * x + m[4 + axis] * y + m[8 + axis] * z + m[12 + axis];
            min[axis] = Math.min(min[axis], value);
            max[axis] = Math.max(max[axis], value);
          }
        }
      }
    }
    return { min, max };
  }
}

export function hsIsHlodManifest(value: unknown): value is HsHlodTilesetManifest {
  return (
    typeof value === 'object' && value !== null && (value as { format?: unknown }).format === HS_HLOD_MANIFEST_FORMAT
  );
}

function assertManifest(manifest: HsHlodTilesetManifest): void {
  if (manifest.format !== HS_HLOD_MANIFEST_FORMAT || manifest.version !== 1 || !Array.isArray(manifest.nodes)) {
    throw new Error(`Unsupported HLOD manifest ${manifest.format} v${manifest.version}.`);
  }
}
